import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SubscriptionGate from '../../components/subscription/SubscriptionGate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Award, FileText, CheckCircle2, Clock, ShieldCheck, AlertCircle, Info, Download, FileJson, Search, X, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const USPTO_FILING_FEE = 350;
const CORPCRAFT_SERVICE_FEE = 150;
const TOTAL_FEE = USPTO_FILING_FEE + CORPCRAFT_SERVICE_FEE;

// USPTO International Classes (common subset)
const IC_CLASSES = [
  { code: '009', name: 'Electronics, computer software, downloadable apps' },
  { code: '016', name: 'Paper goods, printed matter, publications' },
  { code: '025', name: 'Clothing, footwear, headwear' },
  { code: '035', name: 'Advertising, business services, retail store services' },
  { code: '036', name: 'Insurance, financial affairs, real estate' },
  { code: '041', name: 'Education, training, entertainment, sporting activities' },
  { code: '042', name: 'Scientific and technology services, SaaS, software development' },
  { code: '043', name: 'Services providing food and drink, temporary accommodation' },
  { code: '044', name: 'Medical, veterinary, hygienic and beauty care services' },
  { code: '045', name: 'Legal services, security services, personal services' },
];

const ENTITY_TYPES = [
  'Individual',
  'Corporation',
  'LLC',
  'Partnership',
  'Sole Proprietorship',
  'Trust',
  'Non-Profit'
];

export default function TrademarkFilingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filings, setFilings] = useState([]);
  const [clearanceLoading, setClearanceLoading] = useState(false);
  const [clearanceResult, setClearanceResult] = useState(null);
  const [showClearanceModal, setShowClearanceModal] = useState(false);
  const [formData, setFormData] = useState({
    // Mark Details
    mark_name: '',
    mark_type: 'standard_character',
    design_description: '',

    // Applicant Info
    applicant_name: '',
    applicant_entity_type: 'LLC',
    applicant_state: '',
    applicant_country: 'United States',
    applicant_street: '',
    applicant_city: '',
    applicant_zip: '',

    // Goods / Services — multi-class supported
    ic_classes: ['042'],
    goods_services_description: '',

    // Filing Basis
    filing_basis: '1a',
    first_use_date: '',
    first_use_commerce_date: '',
    specimen_description: '',
    specimen_image_base64: '',

    // Correspondence
    correspondence_email: '',
    correspondence_phone: ''
  });

  useEffect(() => {
    loadFilings();
  }, []);

  const loadFilings = async () => {
    try {
      const res = await api.get('/trademark/filings');
      setFilings(res.data.filings || []);
    } catch (err) {
      console.error('Failed to load filings', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleClearanceSearch = async () => {
    if (!formData.mark_name.trim()) {
      toast.error('Enter the mark name first');
      return;
    }
    setClearanceLoading(true);
    setClearanceResult(null);
    try {
      const res = await api.post('/trademark/clearance-search', {
        mark_name: formData.mark_name.trim(),
        ic_class: formData.ic_classes[0] || '042',
      });
      setClearanceResult(res.data);
      setShowClearanceModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clearance search failed');
    } finally {
      setClearanceLoading(false);
    }
  };

  const toggleIcClass = (code) => {
    setFormData((prev) => {
      const has = prev.ic_classes.includes(code);
      const next = has
        ? prev.ic_classes.filter((c) => c !== code)
        : [...prev.ic_classes, code];
      return { ...prev, ic_classes: next.length > 0 ? next : [code] };
    });
  };

  const handleSpecimenUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Specimen image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData({ ...formData, specimen_image_base64: reader.result });
      toast.success('Specimen attached — will be uploaded on save');
    };
    reader.readAsDataURL(file);
  };

  const numClasses = formData.ic_classes.length;
  const estFeeUspto = 350 * numClasses;
  const estFeeService = 150 + 50 * Math.max(0, numClasses - 1);
  const estFeeTotal = estFeeUspto + estFeeService;

  const validateStep = (s) => {
    if (s === 1) {
      if (!formData.mark_name.trim()) {
        toast.error('Mark name is required');
        return false;
      }
      if (formData.mark_type !== 'standard_character' && !formData.design_description.trim()) {
        toast.error('Design description required for stylized/design marks');
        return false;
      }
    }
    if (s === 2) {
      const required = ['applicant_name', 'applicant_state', 'applicant_street', 'applicant_city', 'applicant_zip'];
      for (const f of required) {
        if (!formData[f].trim()) {
          toast.error(`Applicant ${f.replace('applicant_', '').replace('_', ' ')} is required`);
          return false;
        }
      }
    }
    if (s === 3) {
      if (formData.ic_classes.length === 0) {
        toast.error('Select at least one International Class');
        return false;
      }
      if (!formData.goods_services_description.trim() || formData.goods_services_description.length < 20) {
        toast.error('Goods/services description must be at least 20 characters');
        return false;
      }
      if (formData.filing_basis === '1a' && !formData.first_use_date) {
        toast.error('First use date required for "Use in Commerce" filing basis');
        return false;
      }
    }
    if (s === 4) {
      if (!formData.correspondence_email.trim()) {
        toast.error('Correspondence email required');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) setStep(step + 1);
  };

  const handleBack = () => setStep(Math.max(1, step - 1));

  const handleSubmitDraft = async () => {
    if (!validateStep(4)) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        ic_classes: formData.ic_classes,
        ic_class: formData.ic_classes[0],
      };
      delete payload.specimen_image_base64;
      const res = await api.post('/trademark/file', payload);
      const filingId = res.data.filing_id;

      // If user attached a specimen, upload it now
      if (formData.specimen_image_base64) {
        try {
          await api.post(`/trademark/filings/${filingId}/specimen`, {
            image_base64: formData.specimen_image_base64,
            specimen_description: formData.specimen_description,
          });
        } catch (err) {
          console.error('Specimen upload failed', err);
          toast.warning('Filing saved, but specimen upload failed. You can re-upload later.');
        }
      }

      toast.success('Filing draft saved. Proceed to payment to submit.');
      navigate(`/checkout/trademark_filing`, { state: { filing_id: filingId, mark_name: formData.mark_name, fee_total: res.data.fee_total } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save filing');
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      draft: { label: 'Draft', cls: 'bg-slate-600' },
      pending_payment: { label: 'Pending Payment', cls: 'bg-amber-600' },
      submitted: { label: 'Submitted to USPTO', cls: 'bg-blue-600' },
      published: { label: 'Published', cls: 'bg-purple-600' },
      registered: { label: 'Registered', cls: 'bg-emerald-600' }
    };
    const m = map[status] || { label: status, cls: 'bg-slate-600' };
    return <Badge className={`${m.cls} text-white`}>{m.label}</Badge>;
  };

  const handleDownloadPDF = async (filingId, markName) => {
    try {
      const token = localStorage.getItem('corpcraft_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/trademark/filings/${filingId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (markName || 'trademark').replace(/[^a-zA-Z0-9_-]/g, '');
      a.download = `TEAS_${safe}_${filingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('TEAS PDF downloaded');
    } catch (err) {
      toast.error(err.message || 'Failed to download PDF');
    }
  };

  const handleDownloadJSON = async (filingId, markName) => {
    try {
      const res = await api.get(`/trademark/filings/${filingId}/teas-json`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (markName || 'trademark').replace(/[^a-zA-Z0-9_-]/g, '');
      a.download = `TEAS_${safe}_${filingId.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('TEAS JSON downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download JSON');
    }
  };

  const stepLabels = ['Mark', 'Applicant', 'Goods & Basis', 'Review & Pay'];

  return (
    <DashboardLayout>
      <SubscriptionGate featureName="USPTO Trademark Filing">
        <div className="space-y-6 max-w-6xl" data-testid="trademark-filing-page">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white font-['Outfit'] flex items-center gap-3">
                <Award className="w-8 h-8 text-blue-400" />
                USPTO Trademark Filing
              </h1>
              <p className="text-slate-400 mt-2">
                File your federal trademark application with CorpCraft. Fees scale with the number of IC classes selected ($350 USPTO + service fee per class).
              </p>
            </div>
          </div>

          {/* Progress Stepper */}
          <div className="flex items-center gap-2" data-testid="trademark-stepper">
            {stepLabels.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const complete = step > n;
              return (
                <div key={label} className="flex items-center flex-1">
                  <div className={`flex items-center gap-3 px-4 py-2 rounded-lg flex-1 ${
                    active ? 'bg-blue-600/20 border border-blue-500' :
                    complete ? 'bg-emerald-600/10 border border-emerald-600/40' :
                    'bg-slate-800/40 border border-slate-700'
                  }`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                      active ? 'bg-blue-500 text-white' :
                      complete ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {complete ? <CheckCircle2 className="w-4 h-4" /> : n}
                    </div>
                    <span className={`text-sm ${active ? 'text-white font-medium' : complete ? 'text-emerald-300' : 'text-slate-400'}`}>
                      {label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Form Card */}
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="p-8">
              {/* Step 1: Mark Details */}
              {step === 1 && (
                <div className="space-y-6" data-testid="step-mark">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-1">Mark Details</h2>
                    <p className="text-sm text-slate-400">What are you registering?</p>
                  </div>

                  <div>
                    <Label htmlFor="mark_name" className="text-slate-200">Mark Name / Wording *</Label>
                    <Input
                      id="mark_name"
                      data-testid="input-mark-name"
                      value={formData.mark_name}
                      onChange={(e) => handleChange('mark_name', e.target.value)}
                      placeholder="e.g., CORPCRAFT"
                      className="bg-slate-800 border-slate-700 text-white mt-2"
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter the exact wording of the trademark (case sensitive).</p>
                  </div>

                  {/* Clearance Search CTA */}
                  <div className="p-4 bg-gradient-to-r from-emerald-600/10 to-blue-600/10 border border-emerald-500/30 rounded-lg flex items-center justify-between gap-4" data-testid="clearance-cta">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Search className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white">Free Trademark Clearance Search</div>
                        <p className="text-xs text-slate-300 mt-1">
                          Before paying $500, check if your mark conflicts with existing USPTO registrations. Recommended to reduce refusal risk.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      onClick={handleClearanceSearch}
                      disabled={clearanceLoading || !formData.mark_name.trim()}
                      data-testid="btn-run-clearance"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                    >
                      {clearanceLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" />Run Search</>
                      )}
                    </Button>
                  </div>

                  <div>
                    <Label className="text-slate-200">Mark Type *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                      {[
                        { id: 'standard_character', label: 'Standard Character', desc: 'Text only, no design' },
                        { id: 'stylized', label: 'Stylized Text', desc: 'Text with specific font or color' },
                        { id: 'design', label: 'Design Mark', desc: 'Logo or design with/without words' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`mark-type-${opt.id}`}
                          onClick={() => handleChange('mark_type', opt.id)}
                          className={`text-left p-4 rounded-lg border transition ${
                            formData.mark_type === opt.id
                              ? 'bg-blue-600/20 border-blue-500'
                              : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className="font-medium text-white">{opt.label}</div>
                          <div className="text-xs text-slate-400 mt-1">{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {formData.mark_type !== 'standard_character' && (
                    <div>
                      <Label htmlFor="design_description" className="text-slate-200">Design Description *</Label>
                      <Textarea
                        id="design_description"
                        data-testid="input-design-description"
                        value={formData.design_description}
                        onChange={(e) => handleChange('design_description', e.target.value)}
                        placeholder="Describe colors, shapes, stylization (e.g., 'The mark consists of the word CORPCRAFT in blue sans-serif font above a stylized crown design')."
                        className="bg-slate-800 border-slate-700 text-white mt-2 min-h-[100px]"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Applicant */}
              {step === 2 && (
                <div className="space-y-6" data-testid="step-applicant">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-1">Applicant Information</h2>
                    <p className="text-sm text-slate-400">Who owns this trademark?</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="applicant_name" className="text-slate-200">Legal Name of Applicant *</Label>
                      <Input
                        id="applicant_name"
                        data-testid="input-applicant-name"
                        value={formData.applicant_name}
                        onChange={(e) => handleChange('applicant_name', e.target.value)}
                        placeholder="e.g., CorpCraft Legal, Inc."
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="applicant_entity_type" className="text-slate-200">Entity Type *</Label>
                      <select
                        id="applicant_entity_type"
                        data-testid="select-entity-type"
                        value={formData.applicant_entity_type}
                        onChange={(e) => handleChange('applicant_entity_type', e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 mt-2"
                      >
                        {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="applicant_state" className="text-slate-200">State of Formation *</Label>
                      <Input
                        id="applicant_state"
                        data-testid="input-applicant-state"
                        value={formData.applicant_state}
                        onChange={(e) => handleChange('applicant_state', e.target.value)}
                        placeholder="e.g., Delaware"
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="applicant_street" className="text-slate-200">Street Address *</Label>
                      <Input
                        id="applicant_street"
                        data-testid="input-applicant-street"
                        value={formData.applicant_street}
                        onChange={(e) => handleChange('applicant_street', e.target.value)}
                        placeholder="123 Main St, Suite 400"
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="applicant_city" className="text-slate-200">City *</Label>
                      <Input
                        id="applicant_city"
                        data-testid="input-applicant-city"
                        value={formData.applicant_city}
                        onChange={(e) => handleChange('applicant_city', e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="applicant_zip" className="text-slate-200">ZIP / Postal Code *</Label>
                      <Input
                        id="applicant_zip"
                        data-testid="input-applicant-zip"
                        value={formData.applicant_zip}
                        onChange={(e) => handleChange('applicant_zip', e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="applicant_country" className="text-slate-200">Country</Label>
                      <Input
                        id="applicant_country"
                        data-testid="input-applicant-country"
                        value={formData.applicant_country}
                        onChange={(e) => handleChange('applicant_country', e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Goods / Services & Basis */}
              {step === 3 && (
                <div className="space-y-6" data-testid="step-goods">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-1">Goods & Services</h2>
                    <p className="text-sm text-slate-400">What does the mark cover, and how is it used?</p>
                  </div>

                  <div>
                    <Label className="text-slate-200">International Classes * (select one or more)</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 max-h-[320px] overflow-y-auto p-2 bg-slate-800/40 rounded-lg border border-slate-700">
                      {IC_CLASSES.map(c => {
                        const checked = formData.ic_classes.includes(c.code);
                        return (
                          <label
                            key={c.code}
                            data-testid={`ic-class-${c.code}`}
                            className={`flex items-start gap-2 p-2 rounded cursor-pointer transition ${
                              checked ? 'bg-blue-600/20 border border-blue-500' : 'hover:bg-slate-800 border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleIcClass(c.code)}
                              className="mt-1 accent-blue-500"
                            />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-white">IC {c.code}</div>
                              <div className="text-xs text-slate-400">{c.name}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-slate-500">{numClasses} class{numClasses > 1 ? 'es' : ''} selected · USPTO fee $350/class</p>
                      <p className="text-xs text-blue-300 font-semibold" data-testid="fee-estimate">Est. total: ${estFeeTotal.toLocaleString()}</p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="goods_services_description" className="text-slate-200">Goods / Services Description *</Label>
                    <Textarea
                      id="goods_services_description"
                      data-testid="input-goods-description"
                      value={formData.goods_services_description}
                      onChange={(e) => handleChange('goods_services_description', e.target.value)}
                      placeholder="e.g., Software as a service (SaaS) featuring software for corporate entity management, compliance automation, and document signing."
                      className="bg-slate-800 border-slate-700 text-white mt-2 min-h-[120px]"
                    />
                    <p className="text-xs text-slate-500 mt-1">Describe specifically. Vague descriptions get rejected by USPTO examiners.</p>
                  </div>

                  <div>
                    <Label className="text-slate-200">Filing Basis *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      {[
                        { id: '1a', label: 'Section 1(a) — Use in Commerce', desc: 'Mark is already in use in interstate commerce.' },
                        { id: '1b', label: 'Section 1(b) — Intent to Use', desc: 'Bona fide intent to use in commerce in the near future.' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`filing-basis-${opt.id}`}
                          onClick={() => handleChange('filing_basis', opt.id)}
                          className={`text-left p-4 rounded-lg border transition ${
                            formData.filing_basis === opt.id
                              ? 'bg-blue-600/20 border-blue-500'
                              : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className="font-medium text-white">{opt.label}</div>
                          <div className="text-xs text-slate-400 mt-1">{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {formData.filing_basis === '1a' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-800/40 rounded-lg border border-slate-700">
                      <div>
                        <Label htmlFor="first_use_date" className="text-slate-200">Date of First Use Anywhere *</Label>
                        <Input
                          id="first_use_date"
                          data-testid="input-first-use-date"
                          type="date"
                          value={formData.first_use_date}
                          onChange={(e) => handleChange('first_use_date', e.target.value)}
                          className="bg-slate-800 border-slate-700 text-white mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="first_use_commerce_date" className="text-slate-200">Date of First Use in Commerce</Label>
                        <Input
                          id="first_use_commerce_date"
                          data-testid="input-first-use-commerce-date"
                          type="date"
                          value={formData.first_use_commerce_date}
                          onChange={(e) => handleChange('first_use_commerce_date', e.target.value)}
                          className="bg-slate-800 border-slate-700 text-white mt-2"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="specimen_description" className="text-slate-200">Specimen Description</Label>
                        <Textarea
                          id="specimen_description"
                          data-testid="input-specimen-description"
                          value={formData.specimen_description}
                          onChange={(e) => handleChange('specimen_description', e.target.value)}
                          placeholder="e.g., Screenshot of website homepage showing mark used in connection with services."
                          className="bg-slate-800 border-slate-700 text-white mt-2"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label htmlFor="specimen_image" className="text-slate-200">Specimen Image (JPG/PNG, max 5MB)</Label>
                        <input
                          id="specimen_image"
                          data-testid="input-specimen-image"
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={handleSpecimenUpload}
                          className="block w-full text-sm text-slate-200 mt-2 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                        />
                        {formData.specimen_image_base64 && (
                          <div className="mt-3 p-2 bg-slate-800/50 border border-slate-700 rounded inline-block">
                            <img
                              src={formData.specimen_image_base64}
                              alt="Specimen preview"
                              className="max-h-40 rounded"
                              data-testid="specimen-preview"
                            />
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-1">Optional at draft stage — required before USPTO submission. Upload now or after payment.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Review & Pay */}
              {step === 4 && (
                <div className="space-y-6" data-testid="step-review">
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-1">Review & Correspondence</h2>
                    <p className="text-sm text-slate-400">Confirm details, then proceed to payment.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="correspondence_email" className="text-slate-200">Correspondence Email *</Label>
                      <Input
                        id="correspondence_email"
                        data-testid="input-correspondence-email"
                        type="email"
                        value={formData.correspondence_email}
                        onChange={(e) => handleChange('correspondence_email', e.target.value)}
                        placeholder="trademarks@yourcompany.com"
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="correspondence_phone" className="text-slate-200">Correspondence Phone</Label>
                      <Input
                        id="correspondence_phone"
                        data-testid="input-correspondence-phone"
                        value={formData.correspondence_phone}
                        onChange={(e) => handleChange('correspondence_phone', e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="bg-slate-800 border-slate-700 text-white mt-2"
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 space-y-3">
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Application Summary
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="text-slate-400">Mark</div>
                      <div className="text-white font-medium">{formData.mark_name || '—'}</div>
                      <div className="text-slate-400">Type</div>
                      <div className="text-white capitalize">{formData.mark_type.replace('_', ' ')}</div>
                      <div className="text-slate-400">Applicant</div>
                      <div className="text-white">{formData.applicant_name || '—'} ({formData.applicant_entity_type})</div>
                      <div className="text-slate-400">IC Classes</div>
                      <div className="text-white">{formData.ic_classes.map(c => `IC ${c}`).join(', ') || '—'}</div>
                      <div className="text-slate-400">Filing Basis</div>
                      <div className="text-white">{formData.filing_basis === '1a' ? 'Use in Commerce' : 'Intent to Use'}</div>
                    </div>
                  </div>

                  {/* Fee Breakdown */}
                  <div className="bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded-lg p-6 border border-blue-500/30">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-blue-400" /> Fee Breakdown
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-300">USPTO Filing Fee ({numClasses} class{numClasses > 1 ? 'es' : ''} × $350)</span>
                        <span className="text-white font-medium">${estFeeUspto.toLocaleString()}.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-300">CorpCraft Preparation & Review</span>
                        <span className="text-white font-medium">${estFeeService.toLocaleString()}.00</span>
                      </div>
                      <div className="h-px bg-slate-700 my-2"></div>
                      <div className="flex justify-between text-base">
                        <span className="text-white font-semibold">Total Due</span>
                        <span className="text-white font-bold" data-testid="review-fee-total">${estFeeTotal.toLocaleString()}.00</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex gap-2 items-start">
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-200">
                      By proceeding to payment, you authorize CorpCraft to prepare and submit your USPTO trademark application on your behalf. Refunds are not available once the USPTO filing has been submitted.
                    </p>
                  </div>
                </div>
              )}

              {/* Nav Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={step === 1}
                  data-testid="btn-back"
                  className="border-slate-700 text-slate-300"
                >
                  Back
                </Button>

                {step < 4 ? (
                  <Button
                    onClick={handleNext}
                    data-testid="btn-next"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Next: {stepLabels[step]}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmitDraft}
                    disabled={loading}
                    data-testid="btn-submit-pay"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90 text-white"
                  >
                    {loading ? 'Saving...' : `Save Draft & Pay $${estFeeTotal.toLocaleString()}`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Existing Filings */}
          {filings.length > 0 && (
            <Card className="bg-slate-900 border-slate-700" data-testid="existing-filings">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-400" />
                  Your Trademark Filings
                </CardTitle>
                <CardDescription>Track application status and history</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {filings.map(f => {
                    const paid = f.payment_status && f.payment_status !== 'pending';
                    return (
                      <div
                        key={f.id}
                        data-testid={`filing-${f.id}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-white">{f.mark_name}</div>
                          <div className="text-xs text-slate-400">
                            IC {f.ic_class} • {f.application_id || 'No USPTO ID yet'} • {new Date(f.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {paid && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`btn-download-pdf-${f.id}`}
                                onClick={() => handleDownloadPDF(f.id, f.mark_name)}
                                className="border-slate-700 text-slate-200 hover:bg-slate-700"
                              >
                                <Download className="w-4 h-4 mr-1" /> PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`btn-download-json-${f.id}`}
                                onClick={() => handleDownloadJSON(f.id, f.mark_name)}
                                className="border-slate-700 text-slate-200 hover:bg-slate-700"
                              >
                                <FileJson className="w-4 h-4 mr-1" /> JSON
                              </Button>
                            </>
                          )}
                          {statusBadge(f.status)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Clearance Results Modal */}
          {showClearanceModal && clearanceResult && (
            <div
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto"
              onClick={() => setShowClearanceModal(false)}
              data-testid="clearance-modal"
            >
              <div
                className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full my-8 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-6 border-b border-slate-800">
                  <div>
                    <h3 className="text-xl font-bold text-white">Clearance Search Results</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      {clearanceResult.mark_name} · IC {clearanceResult.ic_class} · searched {clearanceResult.corpus_size.toLocaleString()} marks
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowClearanceModal(false)}
                    data-testid="btn-close-clearance"
                    className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Verdict */}
                  <div
                    data-testid="clearance-verdict"
                    className={`p-5 rounded-xl border ${
                      clearanceResult.verdict.color === 'red'
                        ? 'bg-red-500/10 border-red-500/40'
                        : clearanceResult.verdict.color === 'amber'
                        ? 'bg-amber-500/10 border-amber-500/40'
                        : 'bg-emerald-500/10 border-emerald-500/40'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {clearanceResult.verdict.color === 'red' ? (
                        <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                      ) : clearanceResult.verdict.color === 'amber' ? (
                        <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <h4 className={`text-lg font-semibold ${
                          clearanceResult.verdict.color === 'red' ? 'text-red-300' :
                          clearanceResult.verdict.color === 'amber' ? 'text-amber-300' : 'text-emerald-300'
                        }`}>
                          {clearanceResult.verdict.headline}
                        </h4>
                        <p className="text-sm text-slate-200 mt-2 leading-relaxed">{clearanceResult.verdict.summary}</p>
                      </div>
                    </div>
                  </div>

                  {/* Conflict matches */}
                  {clearanceResult.results.length > 0 ? (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-white">
                          Potential Conflicts ({clearanceResult.result_count})
                        </h4>
                        <p className="text-xs text-slate-500">Ranked by risk level</p>
                      </div>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                        {clearanceResult.results.map((r, idx) => (
                          <div
                            key={idx}
                            data-testid={`clearance-match-${idx}`}
                            className="p-4 rounded-lg bg-slate-800/60 border border-slate-700 flex items-start justify-between gap-4"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-white">{r.mark}</span>
                                <Badge className={`text-xs ${
                                  r.status === 'LIVE' ? 'bg-emerald-700' :
                                  r.status === 'PENDING' ? 'bg-amber-700' : 'bg-slate-700'
                                } text-white`}>{r.status}</Badge>
                                {r.same_class && (
                                  <Badge className="bg-blue-700 text-white text-xs">Same Class</Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 mt-1">
                                {r.owner} · IC {r.ic_class}{r.serial_number ? ` · Serial ${r.serial_number}` : ''}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-sm font-bold ${
                                r.risk_color === 'red' ? 'text-red-400' :
                                r.risk_color === 'amber' ? 'text-amber-400' : 'text-emerald-400'
                              }`}>
                                {r.similarity_pct}%
                              </div>
                              <div className={`text-xs mt-0.5 ${
                                r.risk_color === 'red' ? 'text-red-400/80' :
                                r.risk_color === 'amber' ? 'text-amber-400/80' : 'text-emerald-400/80'
                              }`}>
                                {r.risk_level}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                      <p>No matching marks found in our USPTO corpus for your search.</p>
                    </div>
                  )}

                  {/* Deep-scan upsell */}
                  <div className="bg-gradient-to-br from-purple-600/15 to-blue-600/15 border border-purple-500/40 rounded-xl p-5" data-testid="deep-scan-upsell">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="w-5 h-5 text-purple-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-white">Deep-Scan Clearance</h4>
                          <Badge className="bg-purple-600 text-white text-xs">PREMIUM $49</Badge>
                        </div>
                        <p className="text-xs text-slate-300 mt-1">
                          Upgrade to attorney-grade clearance with USPTO TSDR prosecution history, Madrid Protocol (international), phonetic variants, and domain availability. Competitive to TrademarkNow ($99+).
                        </p>
                        <Button
                          size="sm"
                          onClick={() => {
                            setShowClearanceModal(false);
                            navigate('/checkout/trademark_deep_scan', { state: { mark_name: clearanceResult.mark_name, ic_class: clearanceResult.ic_class } });
                          }}
                          data-testid="btn-buy-deep-scan"
                          className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          Buy Deep Scan ($49) →
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="text-xs text-slate-500 leading-relaxed border-t border-slate-800 pt-4">
                    {clearanceResult.disclaimer}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-800 bg-slate-900/40 rounded-b-2xl">
                  <Button
                    variant="outline"
                    onClick={() => setShowClearanceModal(false)}
                    data-testid="btn-close-clearance-footer"
                    className="border-slate-700 text-slate-200"
                  >
                    Close
                  </Button>
                  {clearanceResult.verdict.color !== 'red' && (
                    <Button
                      onClick={() => {
                        setShowClearanceModal(false);
                        toast.success('Great! Continue filling out your application.');
                      }}
                      data-testid="btn-continue-filing"
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90 text-white"
                    >
                      Continue to Filing →
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </SubscriptionGate>
    </DashboardLayout>
  );
}
